import { z } from 'zod';

// User input validation schemas
export const audioUploadSchema = z.object({
  file: z.instanceof(File)
    .refine(file => file.size <= 50 * 1024 * 1024, 'File size must be less than 50MB')
    .refine(
      file => ['audio/wav', 'audio/mpeg', 'audio/aiff'].includes(file.type),
      'File must be WAV, MP3, or AIFF format'
    ),
  preset: z.string().min(1, 'Preset is required')
});

export const voiceCloneSchema = z.object({
  text: z.string().min(1, 'Text is required').max(1000, 'Text must be less than 1000 characters'),
  artist: z.string().min(1, 'Artist style is required')
});

export const remixSchema = z.object({
  file: z.instanceof(File)
    .refine(file => file.size <= 50 * 1024 * 1024, 'File size must be less than 50MB'),
  style: z.enum(['house', 'trap', 'dubstep', 'minimal'], {
    errorMap: () => ({ message: 'Invalid remix style' })
  })
});

export const lyricsSchema = z.object({
  theme: z.string().min(1, 'Theme is required').max(100, 'Theme must be less than 100 characters'),
  style: z.enum(['rap', 'pop', 'rock', 'edm'], {
    errorMap: () => ({ message: 'Invalid music style' })
  }),
  mood: z.enum(['happy', 'sad', 'energetic', 'chill'], {
    errorMap: () => ({ message: 'Invalid mood' })
  })
});

// Rate limiting configuration
export const rateLimits = {
  free: {
    maxUploadsPerDay: 3,
    maxDurationMinutes: 10,
    maxFileSize: 25 * 1024 * 1024 // 25MB
  },
  pro: {
    maxUploadsPerDay: 20,
    maxDurationMinutes: 30,
    maxFileSize: 50 * 1024 * 1024 // 50MB
  },
  enterprise: {
    maxUploadsPerDay: -1, // Unlimited
    maxDurationMinutes: 60,
    maxFileSize: 100 * 1024 * 1024 // 100MB
  }
};

// Error handling utilities
export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

export class RateLimitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RateLimitError';
  }
}

export const validateUserLimits = async (userId: string, fileSize: number, subscription: string) => {
  const limits = rateLimits[subscription as keyof typeof rateLimits];
  
  if (fileSize > limits.maxFileSize) {
    throw new ValidationError(`File size exceeds the ${subscription} plan limit`);
  }

  if (limits.maxUploadsPerDay !== -1) {
    // Check daily upload count from database
    const { count } = await supabase
      .from('audio_projects')
      .select('count', { count: 'exact' })
      .eq('user_id', userId)
      .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
      .single();

    if (count >= limits.maxUploadsPerDay) {
      throw new RateLimitError(`Daily upload limit reached for ${subscription} plan`);
    }
  }
};